<!--
  PortalLayout (route-fork PR-R1 + PR-R4 + design-parity PR-1).

  Client Portal chrome. Branding hook via CSS custom properties:
    - `--portal-brand-color` (single-var hook from PR-R1)
    - `--portal-brand-primary`, `--portal-brand-secondary`, and
      `--portal-brand-accent` (PR-R4 multi-var hook)

  PR-1 design-parity: replaces the ad-hoc brand-colored topbar with the
  shared Topbar component. The brand name renders as the identityPill in
  brand-color chrome; the route-derived title fills the title slot.

  Sidebar nav: Overview / Orders / Approvals / Budget / Allowance / Team /
  Settings. Settings (removed for beta as a mock page) came back 2026-10-27
  to host the Company section, where the client owner renames its company.

  Defaults match the ThreadFlow brand so distributors not yet branded
  render the ThreadFlow identity rather than a blank chrome.
-->
<template>
    <div
        class="portal-layout"
        :style="rootStyle"
        data-testid="portal-root"
    >
        <!-- SA client-impersonation banner (card
             https://trello.com/c/8eq8uc8E). Rendered here so it appears while
             a Super Admin is "viewing as" a client on the portal. The banner
             self-hides (v-if store.isActive) for real portal users. -->
        <ImpersonationBanner />
        <Topbar
            eyebrow="Client Portal"
            :title="topbarTitle"
            data-testid="portal-topbar"
        >
            <template #identityPill>
                <span
                    class="portal-topbar__brand"
                    data-testid="portal-topbar-brand"
                >{{ brandName }}</span>
                <template v-if="scope.loaded && !scope.loadError">
                    <span
                        class="portal-topbar__scope-divider"
                        aria-hidden="true"
                    ></span>
                    <PortalScopePill />
                </template>
            </template>
            <!--
              Personal Profile entry point (card
              https://trello.com/c/BxskXBad): an icon in the top-right
              corner NEXT TO Log out, in all four apps.
            -->
            <router-link
                to="/portal/profile"
                class="portal-topbar__profile"
                data-testid="portal-topbar-profile"
                title="Profile"
                aria-label="Profile"
            >
                <v-icon icon="account_circle" size="22" />
            </router-link>
            <button
                type="button"
                class="portal-topbar__logout"
                data-testid="portal-topbar-logout"
                @click="logout"
            >
                Log out
            </button>
        </Topbar>

        <!-- Unscoped portal user: portal.access but no client_id wired up.
             postLoginRedirect resolves to /portal (their only home) so there
             is nowhere to redirect them. Show a friendly empty state instead
             of a broken shell with 403 console errors. -->
        <div
            v-if="unscopedPortalUser"
            class="portal-unscoped"
            data-testid="portal-unscoped-user"
        >
            <span class="material-icons portal-unscoped__icon" aria-hidden="true">link_off</span>
            <p class="portal-unscoped__title">Account not linked to a client</p>
            <p class="portal-unscoped__body">
                Your account isn't linked to a client yet. Contact your
                administrator to get access.
            </p>
        </div>

        <div v-else class="portal-body">
            <aside class="portal-sidebar" data-testid="portal-sidebar">
                <nav>
                    <ul>
                        <li v-for="item in navItems" :key="item.id">
                            <router-link
                                :to="item.to"
                                :data-testid="`portal-nav-${item.id}`"
                            >
                                <span class="portal-nav__label">{{ item.label }}</span>
                                <span
                                    v-if="item.sub"
                                    class="portal-nav__sub"
                                    :data-testid="`portal-nav-${item.id}-sub`"
                                >{{ item.sub }}</span>
                            </router-link>
                        </li>
                    </ul>
                </nav>
            </aside>

            <main class="portal-main">
                <slot />
            </main>
        </div>
    </div>
</template>

<script>
import { describeError } from "@/utils/safeErrorLog";
import Topbar from "@/layouts/Topbar.vue";
import ImpersonationBanner from "@/components/ImpersonationBanner.vue";
import PortalScopePill from "@/components/portal/PortalScopePill.vue";
import { usePortalScopeStore } from "@/stores/portalScope";
import { usePortalNavSummaryStore } from "@/stores/portalNavSummary";
import { useAccountStore } from "@/stores/account";
import { useEnableDepartments } from "@/composables/useEnableDepartments";
import { emitSidebarRefresh } from "@/stores/sidebarRefresh";
import { postLoginRedirect } from "@/router/postLoginRedirect";

const DEFAULT_BRAND_COLOR = "#3b82f6";
const DEFAULT_BRAND_COLORS = Object.freeze({
    primary: "#3b82f6",
    secondary: "#1e40af",
    accent: "#f97316",
});

// Path prefix -> title. Longest-prefix-match first.
// The /portal/team surface hosts BOTH team membership and the client's
// departments, so its label follows the departments feature gate: ON ->
// "Team & Departments" (matching the store editor's section of the same
// name), OFF -> plain "Team". Exported as one constant so the sidebar item
// and the topbar title -- which resolve through different code paths --
// cannot disagree.
const TEAM_LABEL_WITH_DEPARTMENTS = "Team & Departments";
const TEAM_LABEL_WITHOUT_DEPARTMENTS = "Team";

function teamLabel(enableDepartments) {
    return enableDepartments
        ? TEAM_LABEL_WITH_DEPARTMENTS
        : TEAM_LABEL_WITHOUT_DEPARTMENTS;
}

// Path prefix -> title resolver. ``null`` means "derive from the flag".
const PORTAL_TITLE_MAP = [
    ["/portal/overview", "Overview"],
    ["/portal/orders/new", "Add New Order"],
    ["/portal/orders", "Orders"],
    ["/portal/approvals", "Approvals"],
    ["/portal/budget", "Budget"],
    ["/portal/allowance", "Allowance"],
    ["/portal/team", null],
    ["/portal/settings", "Settings"],
    ["/portal", "Overview"],
];

export default {
    name: "PortalLayout",
    components: { Topbar, ImpersonationBanner, PortalScopePill },
    setup() {
        // The GLOBAL portal store scope (header scope switcher, ADR 0055).
        // Shared with the Overview / Orders / Approvals pages so a scope
        // selection here re-scopes those surfaces.
        const scope = usePortalScopeStore();
        // The sidebar at-a-glance sub-line figures. Owned by a store so a
        // routed child (Approvals) can refresh() it live after an action
        // (D6-polish #3) -- the sub-text then updates without a page reload.
        const navSummaryStore = usePortalNavSummaryStore();
        // Account capabilities for post-403 redirect computation.
        const accountStore = useAccountStore();
        // Departments feature gate (SA-global). The Budget page is a
        // department concept -- budgets are keyed on a client_department
        // -- so the nav entry follows the flag. Read through the ONE
        // shared composable; defaults TRUE so the entry never flickers
        // out while the GET is in flight.
        const { enableDepartments } = useEnableDepartments();
        return { scope, navSummaryStore, accountStore, enableDepartments };
    },
    props: {
        // PR-R1 single-var hook. Kept for backward compatibility.
        brandColor: { type: String, default: DEFAULT_BRAND_COLOR },
        // PR-R4 multi-var hook. `null` falls back to the ThreadFlow palette
        // so Phase 1 mounts without a branding payload still render correctly.
        brandColors: { type: Object, default: null },
        brandName: { type: String, default: "ThreadFlow" },
    },
    data() {
        return {
            // Set to true when the user holds portal.access but has no
            // client scope (client_id is NULL). postLoginRedirect resolves
            // back to /portal in this case -- there is no better destination.
            // The layout renders a friendly empty state rather than
            // redirecting or showing a broken shell with 403 console errors.
            unscopedPortalUser: false,
        };
    },
    async mounted() {
        await this.loadScope();
    },
    beforeUnmount() {
        // Tear down the sidebar:refresh subscription (mirror of the mount-time
        // subscribe) so a portal-shell unmount leaves no dangling bus handler.
        if (this._navSummaryUnsubscribe) {
            this._navSummaryUnsubscribe();
            this._navSummaryUnsubscribe = null;
        }
    },
    watch: {
        // A store REQUESTED by URL (?store=<id>) after the shell mounted. The
        // close-reminder magic link lands on /portal/invite/:token, which
        // renders inside this same layout BEFORE the login exists, so the
        // mount-time load ran unauthenticated; AutoLogin then pushes
        // /portal?store=<id> without remounting the layout. Re-run the whole
        // scope load in that case (the store list never loaded), otherwise
        // just ask the scope store to select the requested store, which it
        // does ONLY when the store is one of the caller's.
        "$route.query.store"(requested) {
            if (requested === undefined || requested === null) return;
            if (!this.scope.loaded || this.scope.loadError) {
                this.loadScope();
            } else {
                this.scope.requestStore(requested);
            }
        },

        // D3 gap closure (2026-07-15, Jake-approved net-new): changing the
        // header store-scope pill re-scopes the portal surfaces, so the sidebar
        // nav-summary should refetch too. Emit through the SAME generic
        // mechanism ('portal.scope.changed' is a PORTAL_KEY) rather than a
        // bespoke watcher-to-refresh, keeping one mechanism. Guarded to scoped
        // users so a redirect/unscoped mount does not fire a doomed refetch.
        //
        // The scope.loaded guard suppresses the initial-load null->id
        // transition: a LOCKED user (Store Manager) has selectedStoreId pinned
        // from null to their scoped_store_id DURING the mount-time
        // scope.load(), and the mount-time navSummaryStore.load() already
        // fetches the summary for that scope -- so emitting here would be a
        // spurious announcement that stacks a redundant nav-summary refetch on
        // every locked first mount. Only a USER-DRIVEN pill change (which
        // happens after the initial load completes) should announce a re-scope.
        //
        // flush: 'sync' is load-bearing for the guard: scope.load() assigns
        // selectedStoreId (line ~85) BEFORE it flips loaded=true (line ~94). A
        // default (batched) watcher runs on a later microtask, by which point
        // loaded is already true and the guard cannot distinguish the
        // initial-load transition. Running the handler synchronously with the
        // assignment lets it observe loaded===false mid-load() and bail.
        "scope.selectedStoreId": {
            flush: "sync",
            handler() {
                if (
                    !this.scope.loaded ||
                    this.unscopedPortalUser ||
                    this.scope.loadError
                )
                    {return;}
                emitSidebarRefresh("portal.scope.changed");
            },
        },
    },
    methods: {
        /**
         * Load the caller's role + store list (and, for a scoped user, the
         * sidebar summary). Runs at mount and again when a requested
         * ?store= arrives before any successful load (see the watcher).
         *
         * onForbidden: if the backend returns 403 (caller has no client scope
         * -- SuperAdmin, Distributor staff, or an un-wired shopper/client),
         * we determine their correct home:
         *   - SA -> /admin, Distributor -> /distributor: redirect there.
         *   - portal.access (only home IS /portal): render a friendly empty
         *     state inside the shell. No redirect (would loop). No further
         *     API calls (they would all 403 too).
         */
        async loadScope() {
            await this.scope.load({
                preferredStoreId: this.$route?.query?.store,
                onForbidden: () => {
                    const home = postLoginRedirect({
                        capabilities: this.accountStore.capabilities,
                    });
                    if (home === "/portal") {
                        // The user holds portal.access but has no client scope
                        // (client_id is NULL -- mis-seeded or not yet linked).
                        // postLoginRedirect resolves back to /portal, so there
                        // is nowhere sensible to redirect them. Render a friendly
                        // empty state inside the portal shell instead of
                        // redirecting (which would loop) or leaving a broken
                        // shell with 403 console noise.
                        this.unscopedPortalUser = true;
                    } else {
                        // SA -> /admin, Distributor -> /distributor, etc.
                        // Redirect them to their correct home surface.
                        this.$router.push(home);
                    }
                },
            });

            // Only load nav-summary figures after we know the user has a client
            // scope. Unscoped portal users and redirected users don't need it;
            // suppressing the call avoids a 403 for unscoped callers.
            if (!this.unscopedPortalUser && !this.scope.loadError) {
                this.navSummaryStore.load();
                // Subscribe the nav-summary store to the single sidebar:refresh
                // event (generic delegated mechanism, PR-E). A mutation on a routed
                // child page (Approvals / Team / Budget) emits
                // emitSidebarRefresh('<portal-row-id>'); the store refetches when
                // the id is a row it owns. Torn down in beforeUnmount so no listener
                // survives a shell switch. Only for scoped users -- an unscoped user
                // never loaded the summary and an emit-driven refetch would 403.
                // Once only: a re-run of loadScope (a ?store= after an
                // unauthenticated first load) must not stack a second handler.
                if (!this._navSummaryUnsubscribe) {
                    this._navSummaryUnsubscribe = this.navSummaryStore.subscribe();
                }
            }
        },
        async logout() {
            try {
                await this.$store.dispatch("logout");
                this.scope.reset();
                this.navSummaryStore.reset();
                this.$router.push("/login");
            } catch (e) {
                console.error(describeError(e));
            }
        },
        usd(cents) {
            // Whole-dollar formatting for the compact sidebar sub-line
            // (design shows "$31,529 . 30d" -- no cents).
            return `$${Math.round((cents || 0) / 100).toLocaleString()}`;
        },
        // Approvals sub-line copy (D6-polish #4). Conveys BOTH the
        // awaiting-approval and changes-requested counts concisely:
        //   both>0  -> "3 awaiting · 2 in changes"
        //   only awaiting -> "3 awaiting you"
        //   only changes  -> "2 in changes"
        //   neither  -> "All caught up"
        approvalsSubText(awaiting, changes) {
            const a = awaiting || 0;
            const c = changes || 0;
            if (a && c) return `${a} awaiting · ${c} in changes`;
            if (a) return `${a} awaiting you`;
            if (c) return `${c} in changes`;
            return "All caught up";
        },
    },
    computed: {
        // The sidebar nav model + its at-a-glance sub-line, derived from the
        // nav-summary payload. Budget/Team subs are null for the Store Manager
        // role (the backend suppresses those fields), so their sub-line simply
        // does not render.
        navItems() {
            const s = this.navSummaryStore.summary || {};
            const has = (v) => v !== null && v !== undefined;
            // Feature gate: drop the Budget entry when Departments is OFF.
            // Filtering the assembled list (rather than gating each item
            // inline) keeps the item definitions readable and makes the
            // gate a single, greppable line.
            const items = [
                {
                    id: "overview",
                    label: "Overview",
                    to: "/portal/overview",
                    sub: has(s.total_spend_30d_cents)
                        ? `${this.usd(s.total_spend_30d_cents)} · 30d`
                        : null,
                },
                {
                    id: "orders",
                    // order_count is the client's ALL-TIME order count (what the
                    // Orders page lists), so the sub reads "N total" rather than
                    // the design mock's "N this period" -- the backend figure is
                    // not windowed, and "this period" would misrepresent it.
                    label: "Orders",
                    to: "/portal/orders",
                    sub: has(s.order_count)
                        ? `${s.order_count} total`
                        : null,
                },
                {
                    id: "approvals",
                    label: "Approvals",
                    to: "/portal/approvals",
                    // D6-polish #4: reflect BOTH awaiting-approval AND
                    // changes-requested items. The sub renders only once the
                    // awaiting count is known (has(pending)); the changes count
                    // defaults to 0 so an older payload without the field still
                    // reads sensibly.
                    sub: has(s.pending_approvals)
                        ? this.approvalsSubText(
                              s.pending_approvals,
                              s.changes_requested_approvals || 0,
                          )
                        : null,
                },
                {
                    id: "budget",
                    label: "Budget",
                    to: "/portal/budget",
                    // budget_pct = BUDGET % used (spent/cap), not the allowance
                    // ratio (renamed from allowance_pct, which was the wrong
                    // pool for this sub-line).
                    sub: has(s.budget_pct)
                        ? `${s.budget_pct}% used`
                        : null,
                },
                {
                    id: "allowance",
                    label: "Allowance",
                    to: "/portal/allowance",
                    sub: null,
                },
                {
                    id: "team",
                    // Flag-derived: the page hosts Departments too when the
                    // feature is ON. Never filtered out -- team membership
                    // is not a department concept, so only the LABEL moves.
                    label: teamLabel(this.enableDepartments),
                    to: "/portal/team",
                    sub: has(s.team_access_count)
                        ? `${s.team_access_count} with access`
                        : null,
                },
                {
                    // Company settings (rename the company). No sub-line.
                    id: "settings",
                    label: "Settings",
                    to: "/portal/settings",
                    sub: null,
                },
            ];
            // Budget is NOT filtered out when departments are off (Jake,
            // 2026-08-24). The page stays VALID with the flag off -- it
            // shows ONE overall budget derived from the department rows
            // instead of a per-department table -- and there is no route
            // guard on /portal/budget, so filtering the entry left the
            // page reachable only by typing the URL.
            return items;
        },
        rootStyle() {
            const style = {
                "--portal-brand-color": this.brandColor,
            };
            const colors = this.brandColors || DEFAULT_BRAND_COLORS;
            if (colors.primary) {
                style["--portal-brand-primary"] = colors.primary;
            }
            if (colors.secondary) {
                style["--portal-brand-secondary"] = colors.secondary;
            }
            if (colors.accent) {
                style["--portal-brand-accent"] = colors.accent;
            }
            return style;
        },
        topbarTitle() {
            const path = this.$route?.path || "";
            for (const [prefix, title] of PORTAL_TITLE_MAP) {
                if (path === prefix || path.startsWith(prefix + "/")) {
                    // A null entry defers to the flag-derived label so the
                    // header and the sidebar always read the same thing.
                    return title === null
                        ? teamLabel(this.enableDepartments)
                        : title;
                }
            }
            return "Overview";
        },
    },
};
</script>

<style scoped>
.portal-layout {
    /* Fixed-viewport shell. See DistributorLayout for the full rationale. */
    height: 100%;
    display: flex;
    flex-direction: column;
    background: var(--tf-surface-light);
    overflow: hidden;
}
/*
 * Brand pill in the Topbar's identityPill slot. Renders in the
 * customer's brand color (via the --portal-brand-color CSS var) so a
 * white-label customer reads as their identity, not ThreadFlow's.
 */
.portal-topbar__brand {
    background: var(--portal-brand-color);
    color: var(--tf-fg-on-primary);
    padding: 4px 10px;
    border-radius: 9999px;
    font-size: 12px;
    font-weight: 600;
}
/* Thin vertical rule between the brand pill and the store-scope pill
 * (design chrome.jsx TopBar: a 1px divider before the scope pill). */
.portal-topbar__scope-divider {
    width: 1px;
    height: 22px;
    background: var(--tf-border);
    margin: 0 4px;
}
/* Personal Profile icon, immediately left of Log out. */
.portal-topbar__profile {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: var(--tf-fg-1);
    text-decoration: none;
    margin-right: 8px;
}

.portal-topbar__profile:hover {
    opacity: 0.75;
}

.portal-topbar__logout {
    background: transparent;
    border: 1px solid var(--tf-border-strong);
    color: var(--tf-fg-1);
    border-radius: var(--tf-radius-xs);
    padding: 4px 12px;
    font-size: 13px;
    cursor: pointer;
}
.portal-topbar__logout:hover {
    background: var(--tf-surface-alt);
}
.portal-body { display: flex; flex: 1; min-height: 0; }
.portal-sidebar {
    width: 220px;
    flex-shrink: 0;
    background: var(--tf-bg);
    border-right: 1px solid var(--tf-border);
    padding: 16px 0;
    overflow-y: auto;
}
.portal-sidebar ul {
    list-style: none;
    padding: 0;
    margin: 0;
}
.portal-sidebar li a {
    display: block;
    padding: 8px 16px;
    color: var(--tf-fg-1);
    cursor: pointer;
}
.portal-nav__label {
    display: block;
}
/* At-a-glance sub-line under each nav label (design chrome.jsx Sidebar):
 * small, muted, sitting just below the label. */
.portal-nav__sub {
    display: block;
    font-size: 11px;
    color: var(--tf-fg-4, #6b7785);
    margin-top: 2px;
}
.portal-main {
    flex: 1;
    padding: 24px;
    min-width: 0;
    min-height: 0;
    overflow-y: auto;
}
/* Unscoped-portal-user empty state: centered placeholder inside the
 * portal shell body area. Renders instead of the sidebar + main when
 * the user holds portal.access but has no client_id wired up. */
.portal-unscoped {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 48px 24px;
    text-align: center;
    color: var(--tf-fg-3, #6b7785);
}
.portal-unscoped__icon {
    font-size: 48px;
    color: var(--tf-fg-4);
    margin-bottom: 8px;
}
.portal-unscoped__title {
    font-size: 18px;
    font-weight: 600;
    color: var(--tf-fg-1, #111827);
    margin: 0;
}
.portal-unscoped__body {
    font-size: 14px;
    color: var(--tf-fg-3, #6b7785);
    max-width: 380px;
    margin: 0;
    line-height: 1.5;
}
</style>
